// AGC: PageHeader - Reusable green gradient header for all main pages
// AGC: Consistent design matching Tasks, Lots, and Weather pages
// AGC: Visual Refresh Design System

import React, { ReactNode } from 'react'
import { LucideIcon } from 'lucide-react'

interface PageHeaderProps {
  icon: LucideIcon
  title: string
  actions?: ReactNode  // AGC: Right-side action buttons
  subtitle?: ReactNode  // AGC: Optional subtitle/dropdown below title
  className?: string
}

/**
 * AGC: PageHeader - Standard green gradient header for all main pages
 * AGC: Design matches TasksList.tsx (lines 281-309) and LotesList.tsx (lines 330-363)
 * AGC: Features:
 * AGC: - Green gradient background with decorative circles
 * AGC: - Icon in white semi-transparent box
 * AGC: - Title and optional subtitle
 * AGC: - Action buttons on right side
 * AGC: - Sticky positioning
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  icon: Icon,
  title,
  actions,
  subtitle,
  className = '',
}) => {
  return (
    <header 
      className={`px-4 pt-4 pb-4 sticky top-0 z-10 rounded-b-2xl relative overflow-hidden ${className}`}
      style={{ background: 'linear-gradient(135deg, #0a3d33 0%, #0d4a3e 50%, #1a6b5a 100%)' }}
    >
      {/* AGC: Decorative background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)' }}
        />
        <div 
          className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)' }}
        />
      </div>
      
      {/* AGC: Header content */}
      <div className="relative z-10">
        {/* AGC: Line 1 - Title with icon and actions */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 flex-1">
            {/* AGC: Icon in square box - same style as Lotes/Tasks */}
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-lg font-semibold text-white">
              {title}
            </h1>
          </div>
          
          {/* AGC: Action buttons on right side */}
          {actions && (
            <div className="flex items-center gap-2">
              {actions}
            </div>
          )}
        </div>
        
        {/* AGC: Optional subtitle row (e.g., farm selector, filters) */}
        {subtitle && (
          <div className="flex items-center justify-between">
            {subtitle}
          </div>
        )}
      </div>
    </header>
  )
}

export default PageHeader
